/**
 * [APP-ALERTS 2026-09-04] The App Alerts drawer — faults the app found in itself.
 *
 * Deliberately the SAME card shape as NotificationPanel (the bell), because the user asked for
 * it in those words: same glass panel, same 12px card with a thin top bar, same label/value
 * rows. What differs is only what the card is FOR. The bell hands you work to assign; this
 * hands you something that is broken.
 *
 * COLOUR: restrained on purpose ("simple, no show off"). Severity is carried by a 3px top bar
 * and the small uppercase heading, nothing else — no coloured card bodies, no filled badges.
 * Every colour is defined for BOTH themes explicitly rather than relying on opacity over an
 * unknown background, which is what made green panels read wrong in dark mode before.
 *
 * There is no dismiss button, and that is the design. A card disappears when the fault behind
 * it is actually gone (the server re-checks every 5 minutes and resolves what it can no longer
 * see). A button that only hides the card would turn this into a second inbox nobody reads.
 * [FREELANCER-REPLY 2026-10-23, user] The one exception: a freelancer NAL / Duplicate card has OK (the reply is sent) and Cancel
 * (nothing is sent) — each does the work, and the card goes because the work is done. No click: the card stays.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, keyframes } from 'styled-components';
import { transition } from '../styles/motion';
import { fluidSheet, fluidScrim, fluidCascade, useLandWindow } from './Fluid'; // [FLUID-2 2026-10-12]
import { SkeletonBox } from './Skeleton';
// [LIQUID-GLASS 2026-11-02, user] the panel, cards, bars and buttons use the shared liquid glass recipe
import {
  lgSurface, lgSurfaceShadow, lgCard, lgControl, lgPress, lgChase, lgSquircle, lgHair, lgControlBg, lgTrack, lgTopGlow,
  lgButtonPrimary, lgButtonSecondary, LG_PRESS, LgPressProps,
} from '../styles/liquidGlass';
import { toastSuccess, toastError } from './toastService'; // [FREELANCER-REPLY 2026-10-23]
import { notifyDataChanged } from '../services/realtime'; // [FREELANCER-REPLY 2026-10-23] Application Logs gets the OK / Cancel line
import { fetchAppAlerts, fetchDbConnections, fetchLeadAssignmentLive, AppAlert, AppAlertSeverity, DbConnections, LeadAssignmentLive, sendFreelancerReply, ignoreFreelancerReply } from '../services/api';

/* ── Severity palette ───────────────────────────────────────────────────────
 * One place, both themes, no opacity guesswork. Dark values are lightened so they
 * read against the dark glass; light values are darkened so they stay crisp on a
 * near-white card. */
type Tone = { bar: string; ink: string };

const TONES: Record<AppAlertSeverity, { dark: Tone; light: Tone }> = {
  high: {
    dark:  { bar: 'rgba(248,113,113,0.55)', ink: '#fca5a5' },
    light: { bar: 'rgba(185,28,28,0.45)',   ink: '#b91c1c' },
  },
  medium: {
    dark:  { bar: 'rgba(251,191,36,0.45)',  ink: '#fcd34d' },
    light: { bar: 'rgba(180,83,9,0.40)',    ink: '#a16207' },
  },
  low: {
    dark:  { bar: 'rgba(162,162,162,0.40)', ink: '#d4d4d4' },
    light: { bar: 'rgba(111,116,114,0.35)', ink: '#505553' },
  },
};

/* Wording, not colour — the same in either theme. Plain English rather than
 * HIGH/MEDIUM/LOW, which tells you a rank but not what to do about it. */
const SEVERITY_LABEL: Record<AppAlertSeverity, string> = {
  high: 'Needs attention',
  medium: 'Worth a look',
  low: 'For information',
};

const tone = (theme: any, severity: AppAlertSeverity): Tone =>
  TONES[severity][theme.colorScheme === 'dark' ? 'dark' : 'light'];

/* ── Shell (mirrors NotificationPanel so the two drawers feel identical) ───── */

const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 999;
  pointer-events: ${(p) => (p.$open ? 'auto' : 'none')};
`;

const Backdrop = styled.div<{ $open: boolean }>`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.18);
  ${(p) => fluidScrim(p.$open) /* [FLUID-2 2026-10-12] fades in, out a little quicker */}
`;

/* [LIQUID-GLASS 2026-11-02, user] A frosted glass sheet that floats 10 px clear of the window's top, right and bottom
   edges, with smooth round corners (styles/liquidGlass.ts). Was a flat strip on the right edge with blur(48px)
   saturate(180%). It hides 24 px further than its own width when closed, so its edge and shadow never peek in. */
const Panel = styled.div<{ $open: boolean }>`
  position: absolute;
  top: 10px;
  right: 10px;
  bottom: 10px;
  width: 452px;
  max-width: calc(92vw / var(--app-zoom, 1)); /* [APP-FIT 2026-10-26] the window in page px */
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: ${(p) => p.theme.text};
  ${lgSurface(34)}
  ${lgSquircle(26)}
  /* Shadow only while open: the off-screen sheet's shadow would bleed back into the page's right edge. */
  box-shadow: ${(p) => (p.$open ? lgSurfaceShadow(p) : 'none')};

  /* [PHONE-LAYOUT 2026-10-22] Phones: one full-width sheet, like the Bin and the bell (92vw left a thin strip of
     page beside it and each drawer had its own width). The header's close button closes it.
     [LIQUID-GLASS 2026-11-02] It floats 8 px clear of the screen's edges, with 22 px corners. */
  @media (max-width: 768px) {
    top: 8px;
    right: 8px;
    bottom: 8px;
    width: calc(100% - 16px);
    max-width: none;
    border-radius: 22px;
  }

  /* [FLUID-2 2026-10-12, user] Slides in on the spring (0.34 s), out quicker (0.22 s) — components/Fluid.tsx. */
  ${(p) => fluidSheet(p.$open, 'translateX(calc(100% + 24px))')}
`;

/* The bell's header is brand green. This one is deliberately NEUTRAL — a fault list
 * that shouts in brand colour reads like a promotion, and a red header would make a quiet
 * "for information" card look like an emergency. Severity belongs on the cards.
 * [LIQUID-GLASS 2026-11-02, user] No grey band any more: the title sits on the glass, over a hairline. */
const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 14px 12px 20px;
  flex-shrink: 0;
  border-bottom: 1px solid ${lgHair};

  /* [PHONE-LAYOUT 2026-10-22] Phones: the same header height with the bigger close button. */
  @media (max-width: 768px) {
    padding: 12px 12px 12px 16px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${(p) => p.theme.text};
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
`;

/* [LIQUID-GLASS 2026-11-02] A small glass capsule. */
const Badge = styled.span`
  background: ${lgControlBg};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.28), 0 0 0 1px ${lgHair};
  color: ${(p) => p.theme.text};
  font-size: 11px;
  font-weight: 700;
  min-width: 22px;
  height: 22px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 7px;
`;

/* [LIQUID-GLASS 2026-11-02] A round glass button that squishes and glows where it is pressed. */
const CloseBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgControl}
  ${lgPress}
  position: relative;
  border: none;
  color: ${(p) => p.theme.text};
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 13px;

  /* [PHONE-LAYOUT 2026-10-22] Phones: a 36px tap target. */
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 15px;
  }
`;

const PanelBody = styled.div<{ $land: boolean }>`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 14px 14px 16px;
  /* [LIQUID-GLASS 2026-11-02] Cards fade softly as they scroll up under the header. */
  -webkit-mask-image: linear-gradient(180deg, transparent 0, #000 14px);
  mask-image: linear-gradient(180deg, transparent 0, #000 14px);
  /* [PHONE-LAYOUT 2026-10-22] Phones: 12px around the cards so they get the width. */
  @media (max-width: 768px) {
    padding: 12px;
  }
  /* [FLUID-2 2026-10-12, user] Right after opening (and when the alerts land) the cards glide in from the right, one
     after another, as the panel settles. Only in that short window (useLandWindow). */
  ${(p) => (p.$land ? fluidCascade('> *', 0, 20, 8, 'softSlide') /* [FLUID-8 2026-10-12] soft: readable at once */ : '')}
`;

const EmptyWrap = styled.div`
  text-align: center;
  padding: 48px 12px;
  color: ${(p) => p.theme.muted};
`;

const EmptyIcon = styled.div`
  font-size: 30px;
  margin-bottom: 12px;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4fdc95' : '#1e7c63')};
`;

const EmptyTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${(p) => p.theme.text};
`;

const EmptyNote = styled.div`
  font-size: 12.5px;
  margin-top: 6px;
  color: ${(p) => p.theme.dim};
  line-height: 1.5;
`;

/* ── Card ─────────────────────────────────────────────────────────────────── */

/* [LIQUID-GLASS 2026-11-02, user] A glass card on the glass sheet: see-through, a light line on its top edge, smooth
   corners. No blur of its own (the sheet already blurs the page), which also makes it cheaper than the old 48 px one. */
const Card = styled.div`
  position: relative;
  isolation: isolate;
  margin-bottom: 12px;
  overflow: hidden;
  ${lgCard}
  ${lgSquircle(22)}
  transition: ${transition.hover};
`;

/* [LIQUID-GLASS 2026-11-02, user] Was a 3 px coloured line on top of the card. Now a soft glow of the same colour from
   the card's top edge, behind the words (z-index -1 inside the card). "For information" cards have no glow; the live
   database card glows green while it is normal ($live). */
const GLOW = {
  dark: { high: 'rgba(248,113,113,0.20)', medium: 'rgba(251,191,36,0.16)', live: 'rgba(74,205,139,0.17)', lead: 'rgba(139,156,255,0.19)' },
  light: { high: 'rgba(220,38,38,0.12)', medium: 'rgba(217,119,6,0.12)', live: 'rgba(16,185,129,0.13)', lead: 'rgba(63,81,201,0.12)' },
};
const glowOf = (theme: any, kind: 'high' | 'medium' | 'live' | 'lead' | 'none'): string =>
  (kind === 'none' ? 'none' : lgTopGlow(GLOW[theme.colorScheme === 'dark' ? 'dark' : 'light'][kind]));

const CardTopBar = styled.div<{ $severity: AppAlertSeverity; $live?: boolean }>`
  position: absolute;
  inset: 0 0 auto 0;
  height: 96px;
  z-index: -1;
  pointer-events: none;
  background: ${(p) => glowOf(p.theme, p.$severity === 'low' ? (p.$live ? 'live' : 'none') : p.$severity)};
`;

const CardHeader = styled.div<{ $severity: AppAlertSeverity }>`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  padding: 13px 16px 9px;
  border-bottom: 1px solid ${lgHair};

  /* [PHONE-LAYOUT 2026-10-22] Phones: a long "came back · 4× · since ..." line moves under the severity instead
     of running past the card. */
  @media (max-width: 768px) {
    flex-wrap: wrap;
    row-gap: 2px;
  }
`;

const SeverityLabel = styled.span<{ $severity: AppAlertSeverity }>`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  color: ${(p) => tone(p.theme, p.$severity).ink};
`;

const SeenAt = styled.span`
  font-size: 11px;
  color: ${(p) => p.theme.dim};
  white-space: nowrap;
`;

/* [APP-ALERTS-NO-DETAILS 2026-09-10, user] Bottom padding moved here from CardActions'
 * margin-top, so a card with no Open button still ends with breathing room.
 * [APP-ALERTS-READ-ONLY 2026-09-16, user] There is no action bar on any card now, so this
 * padding is the card's entire bottom edge. */
const CardBody = styled.div`
  padding: 12px 16px 12px;
`;

const CardTitle = styled.div`
  font-size: 13.5px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
  margin-bottom: 6px;
`;

const CardSummary = styled.div`
  font-size: 13px;
  line-height: 1.55;
  color: ${(p) => p.theme.text};
  /* A long charge id or domain must wrap rather than widen the drawer. */
  overflow-wrap: anywhere;
`;

/* [APP-ALERTS-READ-ONLY 2026-09-16, user] CardImpact, CardActions and ActionBtn are GONE.
 * A card is now its title and ONE summary line: no Open button anywhere (the drawer is
 * read-only, you go to the tab yourself), and no second dimmer paragraph underneath.
 * Everything the case needs must therefore be in the summary the server writes — impact is
 * still stored on every app_alerts row, it is simply not shown to anyone. */

/* [FREELANCER-REPLY 2026-10-23, user] The freelancer NAL / Duplicate card's Cancel + OK — the bell's suggestion card bar
   (NotificationPanel CardActions / ActionBtn): Cancel the secondary, OK the green primary, two equal halves. */
const ReplyActions = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px 14px 12px;
  border-top: 1px solid ${lgHair};
`;
// [LIQUID-GLASS 2026-11-02] the glass capsule buttons (squish + press light)
const ReplyBtn = styled.button.attrs<LgPressProps>(LG_PRESS)<{ $primary?: boolean }>`
  ${(p) => (p.$primary ? lgButtonPrimary : lgButtonSecondary)}
  flex: 1;
  @media (max-width: 768px) {
    height: 40px;
  }
`;
const isReplyCard = (a: AppAlert) => a.detector === 'lead_freelancer_reply' && Number(a.context?.dumpId) > 0;

const SectionHeading = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${(p) => p.theme.dim};
  margin: 6px 0 10px;
`;

/* "Came back 4 times" is the single most useful thing on an alert — it turns a one-off
   problem into "this one keeps coming back", which is the pattern that went unnoticed for
   six months on the website corrections. */
const RepeatTag = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#fcd34d' : '#a16207')};
  white-space: nowrap;
`;

/* ── Helpers ──────────────────────────────────────────────────────────────── */

/** "since 4 Sep 09:15" is more useful on a fault than a relative age, because the first thing
 *  you do with a fault is line it up against what else happened at that time. */
function sinceLabel(at: string | null): string {
  if (!at) return '';
  const [date, time] = at.split(' ');
  if (!date) return at;
  const d = new Date(`${date}T${time || '00:00'}:00`);
  if (isNaN(d.getTime())) return at;
  // [DATE-STYLE 2026-09-29, user] "since 24 Sept 15:11" → "since Sep 24, 2026, 3:11 PM" (one date style
  // across the app). Same clock digits as before — built from the same local Date, no zone shift.
  return time ? DAY_TIME_FMT.format(d) : DAY_FMT.format(d);
}
const DAY_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const DAY_TIME_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

/* ── Live database connections card ────────────────────────────────────────────
 * [LIVE-CONNECTIONS 2026-09-30, user] Pinned at the top of the drawer ("Option 2 · Big number" from
 * the preview the user chose). A live READING, not an alert: it is read every 5 s while the drawer
 * is open (never while it is closed or the browser tab is hidden), it is not stored in app_alerts,
 * and it never counts towards the badge. Normal below 80% of the limit, Busy from 80%, Near the
 * limit from 95% — the same 80% line the stored "running out of connections" alert uses.
 * A failed reading keeps the last number and says so; it never turns into a false all-clear. */
const LIVE_POLL_MS = 5000;

/** [LIVE-CONNECTIONS-COUNT 2026-09-30, user] "The app alert should show a count of 2 for both the cards":
 *  the live card is always on screen, so it always counts as one card — in the drawer header, the
 *  heading and the header icon's badge (AppLayout adds it to the server's stored-alert count).
 *  [LEAD-LIVE-CARD 2026-10-21] 2: the database card + the live "Lead assignment" card (which replaced the two stored
 *  "Lead assignment" status cards). */
export const PINNED_CARD_COUNT = 2;
const TIME_FMT = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });
const DAY_SHORT_TIME_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

function connStatus(inUse: number, max: number): { severity: AppAlertSeverity; word: string } {
  const pct = max > 0 ? inUse / max : 0;
  if (pct >= 0.95) return { severity: 'high', word: 'Near the limit' };
  if (pct >= 0.8) return { severity: 'medium', word: 'Busy' };
  return { severity: 'low', word: 'Normal' };
}

/** Green when normal (the drawer's own all-clear green), otherwise the severity ink. */
const liveColor = (theme: any, severity: AppAlertSeverity): string =>
  (severity === 'low' ? (theme.colorScheme === 'dark' ? '#4fdc95' : '#1e7c63') : tone(theme, severity).ink);

function agoLabel(fromMs: number, nowMs: number): string {
  const s = Math.max(0, Math.round((nowMs - fromMs) / 1000));
  if (s < 2) return 'just now';
  if (s < 60) return `${s}s ago`;
  return `${Math.floor(s / 60)}m ago`;
}

/** When the database last restarted — "11:54 AM" today, "Sep 29, 11:54 AM" before that. */
function startedLabel(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toDateString() === new Date().toDateString() ? TIME_FMT.format(d) : DAY_SHORT_TIME_FMT.format(d);
}

const livePulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
`;

const LiveDot = styled.span<{ $severity: AppAlertSeverity; $pulse?: boolean }>`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: 1px;
  background: ${(p) => liveColor(p.theme, p.$severity)};
  box-shadow: 0 0 8px ${(p) => liveColor(p.theme, p.$severity)}; /* [LIQUID-GLASS 2026-11-02] a soft glow */
  ${(p) => p.$pulse && css`animation: ${livePulse} 2s ease-in-out infinite;`}
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

/* Same look as SeenAt, but allowed to wrap: "Couldn't update, retrying · last reading 12s ago"
   is longer than the date the other cards show, and must not be cut off on a phone. */
const LiveMeta = styled(SeenAt)`
  white-space: normal;
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const BigRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px 12px;
  margin-bottom: 10px;
`;

const BigNum = styled.div`
  font-size: 30px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.5px;
  font-variant-numeric: tabular-nums;
  color: ${(p) => p.theme.text};
`;

const BigOf = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0;
  margin-left: 6px;
  color: ${(p) => p.theme.dim};
`;

/* [LIQUID-GLASS 2026-11-02] A glass capsule. */
const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${lgControlBg};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.22), 0 0 0 1px ${lgHair};
  font-size: 11.5px;
  font-weight: 700;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  color: ${(p) => p.theme.text};
`;

/* [LIQUID-GLASS 2026-11-02, user] An 8 px track pressed into the glass; the fill is glossy, glows, and a soft light
   keeps moving along it while the reading is live (lgChase). The fill's length is its width now (it was a scaleX
   transform, which would squash the moving light). */
const Meter = styled.div`
  height: 8px;
  border-radius: 999px;
  overflow: hidden;
  margin-bottom: 8px;
  background: ${lgTrack};
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.28);
`;

const MeterFill = styled.div<{ $severity: AppAlertSeverity; $busy?: boolean }>`
  position: relative;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(255,255,255,0.45), rgba(255,255,255,0) 70%), ${(p) => liveColor(p.theme, p.$severity)};
  box-shadow: 0 0 12px color-mix(in srgb, ${(p) => liveColor(p.theme, p.$severity)} 45%, transparent), inset 0 1px 0 rgba(255,255,255,0.55);
  transition: width 600ms ease;
  ${(p) => p.$busy && lgChase}
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const LiveSmall = styled.div`
  font-size: 12px;
  color: ${(p) => p.theme.dim};
  font-variant-numeric: tabular-nums;
`;

const LiveWarn = styled.span`
  font-weight: 700;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#fcd34d' : '#a16207')};
`;

const LiveConnectionsCard: React.FC<{ open: boolean }> = ({ open }) => {
  const [reading, setReading] = useState<DbConnections | null>(null);
  const [okAt, setOkAt] = useState(0);
  const [failed, setFailed] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!open) return undefined;
    let stopped = false;
    let inFlight = false;
    const read = () => {
      if (stopped || inFlight || document.hidden) return;
      inFlight = true;
      fetchDbConnections()
        .then((r) => {
          if (stopped) return;
          setReading(r);
          setOkAt(Date.now());
          setFailed(false);
        })
        .catch(() => { if (!stopped) setFailed(true); })
        .finally(() => { inFlight = false; });
    };
    read();
    const poll = window.setInterval(read, LIVE_POLL_MS);
    const clock = window.setInterval(() => setNow(Date.now()), 1000);
    const onVisible = () => { if (!document.hidden) read(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      window.clearInterval(poll);
      window.clearInterval(clock);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [open]);

  if (!reading) {
    if (!failed) return <SkeletonBox style={{ height: 118, borderRadius: 12, marginBottom: 14 }} />;
    return (
      <Card>
        <CardTopBar $severity="medium" />
        <CardHeader $severity="medium">
          <SeverityLabel $severity="medium">Live · Database</SeverityLabel>
          <SeenAt><LiveWarn>Couldn't update, retrying</LiveWarn></SeenAt>
        </CardHeader>
        <CardBody>
          <CardSummary>The database connection count could not be read. Trying again every 5 seconds.</CardSummary>
        </CardBody>
      </Card>
    );
  }

  const { severity, word } = connStatus(reading.inUse, reading.max);
  const pct = reading.max > 0 ? Math.round((reading.inUse / reading.max) * 100) : 0;
  const fill = reading.max > 0 ? Math.min(1, reading.inUse / reading.max) : 0;
  const since = startedLabel(reading.startedAt);

  return (
    <Card>
      <CardTopBar $severity={severity} $live />
      <CardHeader $severity={severity}>
        <SeverityLabel $severity={severity}>
          <LiveDot $severity={severity} $pulse={!failed} aria-hidden="true" />Live · Database
        </SeverityLabel>
        <LiveMeta>
          {failed
            ? <><LiveWarn>Couldn't update, retrying</LiveWarn> · last reading {agoLabel(okAt, now)}</>
            : <>Live · updated {agoLabel(okAt, now)}</>}
        </LiveMeta>
      </CardHeader>
      <CardBody>
        <BigRow>
          <BigNum>
            {reading.inUse.toLocaleString('en-US')}
            <BigOf>/ {reading.max.toLocaleString('en-US')} connections</BigOf>
          </BigNum>
          <StatusPill>
            <LiveDot $severity={severity} aria-hidden="true" />{word} · {pct}%
          </StatusPill>
        </BigRow>
        <Meter
          role="meter"
          aria-label="Database connections in use"
          aria-valuemin={0}
          aria-valuemax={reading.max}
          aria-valuenow={reading.inUse}
        >
          <MeterFill $severity={severity} $busy={!failed} style={{ width: `${fill * 100}%` }} />
        </Meter>
        <LiveSmall>
          Highest {reading.peak.toLocaleString('en-US')} · Refused {reading.refused.toLocaleString('en-US')}
          {since && ` · since ${since}`}
        </LiveSmall>
      </CardBody>
    </Card>
  );
};

/* ── Live "Lead assignment" card ─────────────────────────────────────────────────
 * [LEAD-LIVE-CARD 2026-10-21, user] "dont show the text, make it to look like the progress bar where the assigned will be on the
 * left and full row will have the complete rows data for the lead assignment, the same way it is looking in the live database card
 * … make it a little change so that we can distinguish also between the cards … in the same card show the progress for the date
 * status also". Under the database card, read the same way (every 5 s while the drawer is open and the tab is visible; a failed
 * reading keeps the last numbers and says so). TODAY only, two thicker bars in INDIGO (the database card is green / amber / red):
 *   Data Status Fetch   today's Leads Data rows Processed, of all of today's rows
 *   Assign to HubSpot   today's Ready rows Assigned — ROW (deep) then US (light) from the left — of all of today's Ready rows
 * Each with one small line: what the automatic job is doing (running / waiting / next window / could not run). */
const INDIGO = {
  dark: { bar: 'rgba(139,156,255,0.55)', ink: '#aab6ff', fill: '#8b9cff', fill2: '#c9d0ff', track: 'rgba(255,255,255,0.12)' },
  light: { bar: 'rgba(63,81,201,0.45)', ink: '#3f51c9', fill: '#3f51c9', fill2: '#9aa6ee', track: 'rgba(63,81,201,0.12)' },
};
const indigo = (theme: any) => INDIGO[theme.colorScheme === 'dark' ? 'dark' : 'light'];

/* [LIQUID-GLASS 2026-11-02] The soft top glow (see CardTopBar): indigo, or red while something is wrong. */
const LeadTopBar = styled.div<{ $alarm: boolean }>`
  position: absolute;
  inset: 0 0 auto 0;
  height: 96px;
  z-index: -1;
  pointer-events: none;
  background: ${(p) => glowOf(p.theme, p.$alarm ? 'high' : 'lead')};
`;

const LeadLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  color: ${(p) => indigo(p.theme).ink};
  i { margin-right: 6px; font-size: 11px; }
`;

const LeadSection = styled.div`
  & + & {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid ${lgHair};
  }
`;

const LeadHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
`;

const LeadName = styled.div`
  font-size: 12.5px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
`;

const LeadNum = styled.div`
  font-size: 22px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.3px;
  font-variant-numeric: tabular-nums;
  color: ${(p) => p.theme.text};
  white-space: nowrap;
`;

/* [LIQUID-GLASS 2026-11-02, user] Track pressed into the glass, glossy glowing fills, and the moving light on a fill
   whose work is not finished yet ($busy). */
const LeadTrack = styled.div`
  position: relative;
  height: 10px;
  border-radius: 999px;
  overflow: hidden;
  margin-bottom: 7px;
  background: ${lgTrack};
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.28);
`;

const LeadFill = styled.div<{ $light?: boolean; $busy?: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(255,255,255,0.5), rgba(255,255,255,0) 70%), ${(p) => (p.$light ? indigo(p.theme).fill2 : indigo(p.theme).fill)};
  box-shadow: 0 0 10px color-mix(in srgb, ${(p) => indigo(p.theme).fill} 45%, transparent), inset 0 1px 0 rgba(255,255,255,0.55);
  transition: width 600ms ease, left 600ms ease;
  ${(p) => p.$busy && lgChase}
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const LeadFoot = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 4px 12px;
  font-size: 12px;
  line-height: 1.45;
  color: ${(p) => p.theme.dim};
  font-variant-numeric: tabular-nums;
`;

const LeadKey = styled.span<{ $light?: boolean }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%; /* [LIQUID-GLASS 2026-11-02] round, like the bars' ends */
  margin-right: 5px;
  vertical-align: 0;
  background: ${(p) => (p.$light ? indigo(p.theme).fill2 : indigo(p.theme).fill)};
`;

const LeadBad = styled.span`
  font-weight: 700;
  color: ${(p) => tone(p.theme, 'high').ink};
`;

const LeadBusy = styled.span`
  font-weight: 700;
  color: ${(p) => indigo(p.theme).ink};
  animation: ${livePulse} 2s ease-in-out infinite;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const pctOf = (n: number, of: number) => (of > 0 ? Math.max(0, Math.min(100, (n / of) * 100)) : 0);
const partWord = (p: string) => (p === 'ROW' ? 'ROW rows' : 'all rows');
const timeOf = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : TIME_FMT.format(d);
};

const LeadAssignmentCard: React.FC<{ open: boolean }> = ({ open }) => {
  const [reading, setReading] = useState<LeadAssignmentLive | null>(null);
  const [okAt, setOkAt] = useState(0);
  const [failed, setFailed] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!open) return undefined;
    let stopped = false;
    let inFlight = false;
    const read = () => {
      if (stopped || inFlight || document.hidden) return;
      inFlight = true;
      fetchLeadAssignmentLive()
        .then((r) => {
          if (stopped) return;
          setReading(r);
          setOkAt(Date.now());
          setFailed(false);
        })
        .catch(() => { if (!stopped) setFailed(true); })
        .finally(() => { inFlight = false; });
    };
    read();
    const poll = window.setInterval(read, LIVE_POLL_MS);
    const clock = window.setInterval(() => setNow(Date.now()), 1000);
    const onVisible = () => { if (!document.hidden) read(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      window.clearInterval(poll);
      window.clearInterval(clock);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [open]);

  if (!reading) {
    if (!failed) return <SkeletonBox style={{ height: 196, borderRadius: 12, marginBottom: 14 }} />;
    return (
      <Card>
        <LeadTopBar $alarm={false} />
        <CardHeader $severity="low">
          <LeadLabel><i className="fas fa-user-check" aria-hidden="true" />Live · Lead assignment</LeadLabel>
          <SeenAt><LiveWarn>Couldn't update, retrying</LiveWarn></SeenAt>
        </CardHeader>
        <CardBody>
          <CardSummary>Today's lead assignment progress could not be read. Trying again every 5 seconds.</CardSummary>
        </CardBody>
      </Card>
    );
  }

  const { fetch: f, assign: a } = reading;
  const alarm = f.failing || f.stale || a.failing;
  const [, mm, dd] = reading.day.split('-');
  const dayLabel = `${Number(dd)} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(mm) - 1] || ''}`;

  // Data Status Fetch — one line: what the automatic check is doing
  // [AUTO-STATUS-HOURS 2026-10-21] it checks 12:30–6:30 PM IST only — outside: when it starts, or (after) today's rows left are the dropdown's
  const off = f.hours && !f.hours.open ? f.hours : null;
  let fetchLine: React.ReactNode;
  if (!f.on) fetchLine = 'Runs by itself on the live server only';
  else if (f.stale) fetchLine = <LeadBad>Has not run for a while — see its alert</LeadBad>;
  else if (f.failing) fetchLine = <LeadBad>Could not run{f.error ? ` — ${f.error}` : ''}</LeadBad>;
  else if (f.checking) fetchLine = <LeadBusy>Checking now…</LeadBusy>;
  else if (off && !off.tomorrow) fetchLine = `${f.waiting ? `${f.waiting} waiting` : 'All Processed'} · by itself from ${off.from}`;
  else if (off && f.waiting) fetchLine = `${f.waiting} waiting · by itself only ${off.from}–${off.to}, use Data Status Fetch`;
  else if (off) fetchLine = `All Processed${f.lastCheckAt ? ` · checked ${timeOf(f.lastCheckAt)}` : ''} · next tomorrow ${off.from}`;
  else if (f.waiting) fetchLine = `${f.waiting} waiting · ${f.waitedFor ? `waits for ${f.waitedFor}` : 'next check within 5 min'}`;
  else fetchLine = `All Processed${f.lastCheckAt ? ` · checked ${timeOf(f.lastCheckAt)}` : ''}`;

  // Assign to HubSpot — one line: running / could not run / paused / in its window / the next window
  let assignLine: React.ReactNode;
  if (!a.on) assignLine = 'Runs by itself on the live server only';
  else if (a.failing) assignLine = <LeadBad>Could not run — see its alert</LeadBad>;
  else if (a.running) assignLine = <LeadBusy>Running ({partWord(a.running.part)}): {a.running.phase}{a.running.total ? ` ${a.running.done} of ${a.running.total}` : ''}</LeadBusy>;
  else if (a.paused && a.window) assignLine = `Paused until ${a.window.to} (Stop was pressed)`;
  else if (a.window) assignLine = `Now ${a.window.from}–${a.window.to} · ${partWord(a.window.part)} · every 5 min${a.lastRunAt ? ` · last run ${timeOf(a.lastRunAt)}` : ''}`;
  else assignLine = `Next ${a.next.tomorrow ? 'tomorrow ' : ''}${a.next.from}–${a.next.to} · ${partWord(a.next.part)}${a.lastRunAt ? ` · last run ${timeOf(a.lastRunAt)}` : ''}`;
  const notSelf = [a.held ? `${a.held} held` : '', a.noWebsite ? `${a.noWebsite} no website` : '', a.noEmail ? `${a.noEmail} no email` : ''].filter(Boolean);

  const rowPct = pctOf(a.row.assigned, a.ready);
  const usPct = pctOf(a.us.assigned, a.ready);

  return (
    <Card>
      <LeadTopBar $alarm={alarm} />
      <CardHeader $severity="low">
        <LeadLabel><i className="fas fa-user-check" aria-hidden="true" />Live · Lead assignment · {dayLabel}</LeadLabel>
        <LiveMeta>
          {failed
            ? <><LiveWarn>Couldn't update, retrying</LiveWarn> · last reading {agoLabel(okAt, now)}</>
            : <>updated {agoLabel(okAt, now)}</>}
        </LiveMeta>
      </CardHeader>
      <CardBody>
        <LeadSection>
          <LeadHead>
            <LeadName>Data Status Fetch</LeadName>
            <LeadNum>{f.processed.toLocaleString('en-US')}<BigOf>/ {f.total.toLocaleString('en-US')} Processed</BigOf></LeadNum>
          </LeadHead>
          <LeadTrack role="meter" aria-label="Today's Leads Data rows Processed" aria-valuemin={0} aria-valuemax={f.total} aria-valuenow={f.processed}>
            <LeadFill $busy={f.processed < f.total} style={{ left: 0, width: `${pctOf(f.processed, f.total)}%` }} />
          </LeadTrack>
          <LeadFoot>
            <span>{fetchLine}</span>
            {f.noDetails > 0 && <span>{f.noDetails} without email / website</span>}
          </LeadFoot>
        </LeadSection>
        <LeadSection>
          <LeadHead>
            <LeadName>Assign to HubSpot</LeadName>
            <LeadNum>{a.assigned.toLocaleString('en-US')}<BigOf>/ {a.ready.toLocaleString('en-US')} Assigned</BigOf></LeadNum>
          </LeadHead>
          <LeadTrack role="meter" aria-label="Today's Ready leads Assigned" aria-valuemin={0} aria-valuemax={a.ready} aria-valuenow={a.assigned}>
            <LeadFill $busy={a.assigned < a.ready} style={{ left: 0, width: `${rowPct}%` }} />
            <LeadFill $light style={{ left: `${rowPct}%`, width: `${usPct}%` }} />
          </LeadTrack>
          <LeadFoot>
            <span><LeadKey />ROW {a.row.assigned}/{a.row.ready} · <LeadKey $light />US {a.us.assigned}/{a.us.ready}</span>
            {notSelf.length > 0 && <span>Not by itself: {notSelf.join(' · ')}</span>}
          </LeadFoot>
          <LeadFoot style={{ marginTop: 3 }}>
            <span>{assignLine}</span>
          </LeadFoot>
        </LeadSection>
      </CardBody>
    </Card>
  );
};

interface Props {
  open: boolean;
  onClose: () => void;
  /** Lets the parent refresh its badge as soon as the drawer has fresher data. */
  onCounts?: (open: number) => void;
}

const AppAlertsDrawer: React.FC<Props> = ({ open, onClose, onCounts }) => {
  const [alerts, setAlerts] = useState<AppAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const totalCards = alerts.length + PINNED_CARD_COUNT;

  // The drawer shows OPEN alerts only. Resolved ones are still returned by the API and
  // still kept forever in the database — they are simply not surfaced here.
  const load = useCallback(() => {
    setLoading(true);
    fetchAppAlerts()
      .then((data) => {
        setAlerts(data.open);
        onCounts?.(data.open.length + PINNED_CARD_COUNT);
      })
      .catch(() => { setAlerts([]); })
      .finally(() => { setLoading(false); setLoaded(true); });
  }, [onCounts]);

  useEffect(() => {
    if (!open) return;
    load();
  }, [open, load]);

  // [FREELANCER-REPLY 2026-10-23, user] "If I click on OK, then the mail will be sent to that particular mail replying with the proper
  // detail like the NAL or the duplicate. And if I click cancel, then that row will be ignored": the card goes only after the server
  // has done it (sent / already answered in Gmail / ignored); a refusal keeps the card and says why.
  const [replyBusy, setReplyBusy] = useState<string | null>(null);
  const replyBusyRef = useRef(false); // a second click in the same instant (before the buttons re-render disabled) does nothing
  const answerReply = async (a: AppAlert, action: 'send' | 'ignore') => {
    const dumpId = Number(a.context?.dumpId);
    if (!dumpId || replyBusyRef.current) return;
    replyBusyRef.current = true;
    setReplyBusy(`${a.key}|${action}`);
    try {
      const out = action === 'send' ? await sendFreelancerReply(dumpId) : await ignoreFreelancerReply(dumpId);
      const next = alerts.filter((x) => x.key !== a.key);
      setAlerts(next);
      onCounts?.(next.length + PINNED_CARD_COUNT);
      notifyDataChanged(['activityLogs']);
      toastSuccess(out.status === 'sent' ? `Reply sent to ${out.to}: "${out.replyText}"`
        : out.status === 'already' ? `${out.to} was already answered in Gmail — nothing sent`
          : `Ignored — nothing was sent to ${out.to}`);
    } catch (err: any) {
      toastError(err?.response?.data?.error || (action === 'send' ? 'The reply could not be sent' : 'Could not cancel the card'));
    } finally {
      replyBusyRef.current = false;
      setReplyBusy(null);
    }
  };

  // [FLUID-2 2026-10-12] The window in which the cards glide in: the panel opening, then the alerts landing.
  const land = useLandWindow(open, loading ? 'loading' : 'ready');

  // Portal to <body> so the fixed drawer anchors to the viewport regardless of any
  // transformed or filtered ancestor. data-reload-block only while open — this component
  // stays mounted, and an auto-refresh mid-read would lose your place.
  return createPortal(
    <Overlay $open={open} data-reload-block={open ? '' : undefined}>
      <Backdrop $open={open} onClick={onClose} />
      <Panel $open={open} role="dialog" aria-label="App Alerts">
        <PanelHeader>
          <HeaderLeft>
            <i className="fas fa-triangle-exclamation" style={{ fontSize: 15 }} />
            <PanelTitle>App Alerts</PanelTitle>
            <Badge>{totalCards}</Badge>
          </HeaderLeft>
          <CloseBtn onClick={onClose} aria-label="Close">
            <i className="fas fa-times" />
          </CloseBtn>
        </PanelHeader>

        <PanelBody $land={land}>
          {/* [LIVE-CONNECTIONS-COUNT 2026-09-30, user] The heading sits above BOTH kinds of card and
              counts the live card too, so it always agrees with the number in the header. */}
          {loaded && alerts.length > 0 && (
            <SectionHeading>{totalCards} {totalCards === 1 ? 'thing needs' : 'things need'} attention</SectionHeading>
          )}

          {/* [LIVE-CONNECTIONS 2026-09-30, user] Always first. */}
          <LiveConnectionsCard open={open} />
          {/* [LEAD-LIVE-CARD 2026-10-21, user] Always second: today's Data Status Fetch + Assign to HubSpot progress. */}
          <LeadAssignmentCard open={open} />

          {loading && !loaded && (
            <>
              <SkeletonBox style={{ height: 132, borderRadius: 12, marginBottom: 14 }} />
              <SkeletonBox style={{ height: 132, borderRadius: 12, marginBottom: 14 }} />
              <SkeletonBox style={{ height: 132, borderRadius: 12 }} />
            </>
          )}

          {loaded && alerts.length === 0 && (
            <EmptyWrap>
              <EmptyIcon><i className="fas fa-circle-check" /></EmptyIcon>
              <EmptyTitle>Nothing needs your attention</EmptyTitle>
              <EmptyNote>
                Every feed is running and no faults were found.<br />
                The app re-checks itself every 5 minutes.
              </EmptyNote>
            </EmptyWrap>
          )}

          {loaded && alerts.length > 0 && (
            <>
              {alerts.map((a) => {
                return (
                  <Card key={a.key}>
                    <CardTopBar $severity={a.severity} />
                    <CardHeader $severity={a.severity}>
                      <SeverityLabel $severity={a.severity}>
                        {SEVERITY_LABEL[a.severity]}
                      </SeverityLabel>
                      <SeenAt>
                        {/* A recurrence is a different problem from a first occurrence — it
                            means whatever settled it last time did not hold. Say so here
                            rather than only in the history. */}
                        {a.timesSeen > 1 && <RepeatTag>came back · {a.timesSeen}× · </RepeatTag>}
                        {a.firstSeenAt && `since ${sinceLabel(a.firstSeenAt)}`}
                      </SeenAt>
                    </CardHeader>
                    {/* [APP-ALERTS-READ-ONLY 2026-09-16, user] Title + summary and nothing else:
                        no Open button, no dimmer impact paragraph. The card is a short read. */}
                    <CardBody>
                      <CardTitle>{a.title}</CardTitle>
                      <CardSummary>{a.summary}</CardSummary>
                    </CardBody>
                    {isReplyCard(a) && (
                      <ReplyActions data-reply-actions={String(a.context.dumpId)}>
                        <ReplyBtn disabled={replyBusy !== null} onClick={() => answerReply(a, 'ignore')}>
                          {replyBusy === `${a.key}|ignore` ? 'Cancelling…' : 'Cancel'}
                        </ReplyBtn>
                        <ReplyBtn $primary disabled={replyBusy !== null} onClick={() => answerReply(a, 'send')}>
                          {replyBusy === `${a.key}|send` ? 'Sending…' : 'OK'}
                        </ReplyBtn>
                      </ReplyActions>
                    )}
                  </Card>
                );
              })}
            </>
          )}

          {/* Resolved history is deliberately NOT rendered — the drawer is for what still needs
              attention. The records themselves are untouched: the API still returns them and the
              database still keeps every one forever. */}
        </PanelBody>
      </Panel>
    </Overlay>,
    document.body,
  );
};

export default AppAlertsDrawer;
